<template>
  <div>
    <NavBar pageName="Service Limitations" />
    <v-container style="margin-top: 185px;">
      <v-btn variant="text" size="small" @click="$router.push('/settings/services')" class="mb-2">
        <v-icon size="small" class="mr-1">mdi-arrow-left</v-icon>
        Services Settings
      </v-btn>
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          <span>
            <v-icon class="mr-2">mdi-speedometer</v-icon>
            Limitari Servicii per SIM
          </span>
          <div>
            <v-btn color="secondary" variant="outlined" class="mr-2" @click="loadLimitations">
              <v-icon class="mr-1">mdi-refresh</v-icon>
              Refresh
            </v-btn>
            <v-btn color="success" @click="openAddDialog">
              <v-icon class="mr-1">mdi-plus</v-icon>
              Adauga Limitare
            </v-btn>
          </div>
        </v-card-title>
        <v-card-text>
          <v-alert type="info" variant="tonal" density="compact" class="mb-4">
            Limitarile definesc de cate ori un serviciu poate fi aplicat pe un SIM intr-o anumita perioada.
            Exemplu: serviciul "Credit TopUp" poate fi aplicat maximum 3 ori pe luna pe acelasi SIM.
          </v-alert>

          <v-data-table
            :headers="headers"
            :items="limitations"
            :loading="loading"
            :items-per-page="20"
          >
            <template #[`item.active`]="{ item }">
              <v-chip :color="item.active ? 'success' : 'grey'" size="small">
                {{ item.active ? 'Activ' : 'Inactiv' }}
              </v-chip>
            </template>
            <template #[`item.apply_to_subclients_only`]="{ item }">
              <v-chip :color="item.apply_to_subclients_only ? 'info' : 'warning'" size="small">
                {{ item.apply_to_subclients_only ? 'Doar subclienți' : 'Toți' }}
              </v-chip>
            </template>
            <template #[`item.actions`]="{ item }">
              <v-btn size="x-small" color="primary" class="mr-1" @click="openEditDialog(item)">
                <v-icon size="small">mdi-pencil</v-icon>
              </v-btn>
              <v-btn size="x-small" color="error" @click="confirmDelete(item)">
                <v-icon size="small">mdi-delete</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>

      <!-- Add/Edit Dialog -->
      <v-dialog v-model="formDialog" max-width="600">
        <v-card>
          <v-card-title :class="isEditing ? 'bg-primary' : 'bg-success'" class="text-white">
            <v-icon class="mr-2">{{ isEditing ? 'mdi-pencil' : 'mdi-plus' }}</v-icon>
            {{ isEditing ? 'Editeaza Limitare' : 'Adauga Limitare Noua' }}
          </v-card-title>
          <v-card-text class="pt-4">
            <!-- Client Selection (only for add) -->
            <v-autocomplete
              v-if="!isEditing"
              v-model="form.client_id"
              :items="clients"
              item-title="name"
              item-value="id"
              label="Client/Distribuitor"
              :rules="[v => !!v || 'Clientul este obligatoriu']"
              density="compact"
              variant="outlined"
              class="mb-3"
              :loading="loadingClients"
            />
            <div v-else class="mb-3">
              <strong>Client:</strong> {{ form.client_name }}
            </div>

            <!-- Service Selection -->
            <v-autocomplete
              v-if="!isEditing"
              v-model="form.service_id"
              :items="services"
              item-title="name"
              item-value="id"
              label="Serviciu"
              :rules="[v => !!v || 'Serviciul este obligatoriu']"
              density="compact"
              variant="outlined"
              class="mb-3"
              :loading="loadingServices"
            >
              <template #item="{ props, item }">
                <v-list-item v-bind="props">
                  <template #append>
                    <v-chip size="x-small" color="primary">{{ item.raw.service_type_name }}</v-chip>
                  </template>
                </v-list-item>
              </template>
            </v-autocomplete>
            <div v-else class="mb-3">
              <strong>Serviciu:</strong> {{ form.service_name }}
            </div>

            <!-- Billing Period -->
            <v-select
              v-model="form.billing_period_id"
              :items="billingPeriods"
              item-title="name"
              item-value="id"
              label="Perioada"
              :rules="[v => !!v || 'Perioada este obligatorie']"
              density="compact"
              variant="outlined"
              class="mb-3"
              :loading="loadingPeriods"
            />

            <!-- Max Usages -->
            <v-text-field
              v-model.number="form.max_usages"
              label="Numar maxim de aplicari per SIM"
              type="number"
              min="1"
              :rules="[v => v > 0 || 'Numarul trebuie sa fie mai mare ca 0']"
              density="compact"
              variant="outlined"
              class="mb-3"
            />

            <!-- Apply to subclients only -->
            <v-checkbox
              v-model="form.apply_to_subclients_only"
              label="Aplica doar subclientilor (nu si distribuitorului)"
              density="compact"
              class="mb-3"
            />

            <!-- Active -->
            <v-checkbox
              v-model="form.active"
              label="Limitare activa"
              density="compact"
              class="mb-3"
            />

            <!-- Description -->
            <v-textarea
              v-model="form.description"
              label="Descriere (optional)"
              rows="2"
              density="compact"
              variant="outlined"
            />
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn @click="formDialog = false">Anuleaza</v-btn>
            <v-btn
              :color="isEditing ? 'primary' : 'success'"
              @click="saveLimitation"
              :loading="saving"
              :disabled="!isFormValid"
            >
              {{ isEditing ? 'Salveaza' : 'Adauga' }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Delete Confirmation Dialog -->
      <v-dialog v-model="deleteDialog" max-width="400">
        <v-card>
          <v-card-title class="bg-error text-white">
            <v-icon class="mr-2">mdi-alert</v-icon>
            Confirma Stergerea
          </v-card-title>
          <v-card-text class="pt-4">
            Sigur doriti sa stergeti limitarea pentru serviciul
            <strong>{{ limitationToDelete?.service_name }}</strong>
            aplicata clientului <strong>{{ limitationToDelete?.client_name }}</strong>?
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn @click="deleteDialog = false">Anuleaza</v-btn>
            <v-btn color="error" @click="deleteLimitation" :loading="deleting">Sterge</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Snackbar -->
      <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000">
        {{ snackbar.message }}
      </v-snackbar>
    </v-container>
  </div>
</template>

<script>
import NavBar from '@/components/NavBar.vue'
import {
  getClientServicesLimitations,
  createClientServicesLimitation,
  updateClientServicesLimitation,
  deleteClientServicesLimitation,
  getBillingPeriods,
  getServicesForLimitations
} from '@/composables/useClientServicesLimitationsAPI'
import { getClientsList } from '@/composables/useGetClientsAPI'
import store from '@/store'

export default {
  name: 'ClientServicesLimitationsSettings',
  components: { NavBar },
  data() {
    return {
      loading: false,
      limitations: [],
      clients: [],
      services: [],
      billingPeriods: [],
      loadingClients: false,
      loadingServices: false,
      loadingPeriods: false,
      headers: [
        { title: 'ID', key: 'id', width: '60px' },
        { title: 'Client', key: 'client_name' },
        { title: 'Serviciu', key: 'service_name' },
        { title: 'Perioada', key: 'billing_period_name' },
        { title: 'Max Aplicari/SIM', key: 'max_usages', width: '120px' },
        { title: 'Aplica la', key: 'apply_to_subclients_only', width: '130px' },
        { title: 'Status', key: 'active', width: '100px' },
        { title: 'Actiuni', key: 'actions', width: '120px', sortable: false }
      ],
      // Form dialog
      formDialog: false,
      isEditing: false,
      saving: false,
      form: {
        id: null,
        client_id: null,
        client_name: '',
        service_id: null,
        service_name: '',
        billing_period_id: null,
        max_usages: 1,
        active: true,
        apply_to_subclients_only: true,
        description: ''
      },
      // Delete dialog
      deleteDialog: false,
      deleting: false,
      limitationToDelete: null,
      // Snackbar
      snackbar: {
        show: false,
        message: '',
        color: 'success'
      }
    }
  },
  computed: {
    token() {
      return store.getters.getToken
    },
    isFormValid() {
      if (this.isEditing) {
        return this.form.billing_period_id && this.form.max_usages > 0
      }
      return this.form.client_id && this.form.service_id &&
             this.form.billing_period_id && this.form.max_usages > 0
    }
  },
  async mounted() {
    await Promise.all([
      this.loadLimitations(),
      this.loadClients(),
      this.loadServices(),
      this.loadBillingPeriods()
    ])
  },
  methods: {
    async loadLimitations() {
      this.loading = true
      try {
        const result = await getClientServicesLimitations(this.token)
        if (!result.error) {
          this.limitations = result.limitations || []
        } else {
          this.showSnackbar(result.message, 'error')
        }
      } finally {
        this.loading = false
      }
    },
    async loadClients() {
      this.loadingClients = true
      try {
        const result = await getClientsList(this.token)
        if (!result.error) {
          // Get clients list
          this.clients = (result.clients_list || []).map(c => ({
            id: c.id,
            name: c.name,
            is_distributor: c.is_distributor
          }))
        }
      } finally {
        this.loadingClients = false
      }
    },
    async loadServices() {
      this.loadingServices = true
      try {
        const result = await getServicesForLimitations(this.token)
        if (!result.error) {
          this.services = result.services || []
        }
      } finally {
        this.loadingServices = false
      }
    },
    async loadBillingPeriods() {
      this.loadingPeriods = true
      try {
        const result = await getBillingPeriods(this.token)
        if (!result.error) {
          this.billingPeriods = result.billing_periods || []
        }
      } finally {
        this.loadingPeriods = false
      }
    },
    openAddDialog() {
      this.isEditing = false
      this.form = {
        id: null,
        client_id: null,
        client_name: '',
        service_id: null,
        service_name: '',
        billing_period_id: null,
        max_usages: 1,
        active: true,
        apply_to_subclients_only: true,
        description: ''
      }
      this.formDialog = true
    },
    openEditDialog(limitation) {
      this.isEditing = true
      this.form = {
        id: limitation.id,
        client_id: limitation.client_id,
        client_name: limitation.client_name,
        service_id: limitation.service_id,
        service_name: limitation.service_name,
        billing_period_id: limitation.billing_period_id,
        max_usages: limitation.max_usages,
        active: limitation.active,
        apply_to_subclients_only: limitation.apply_to_subclients_only,
        description: limitation.description || ''
      }
      this.formDialog = true
    },
    async saveLimitation() {
      if (!this.isFormValid) return

      this.saving = true
      try {
        let result
        if (this.isEditing) {
          result = await updateClientServicesLimitation(this.token, this.form.id, {
            billing_period_id: this.form.billing_period_id,
            max_usages: this.form.max_usages,
            active: this.form.active,
            apply_to_subclients_only: this.form.apply_to_subclients_only,
            description: this.form.description
          })
        } else {
          result = await createClientServicesLimitation(this.token, {
            client_id: this.form.client_id,
            service_id: this.form.service_id,
            billing_period_id: this.form.billing_period_id,
            max_usages: this.form.max_usages,
            active: this.form.active,
            apply_to_subclients_only: this.form.apply_to_subclients_only,
            description: this.form.description
          })
        }

        if (!result.error) {
          this.showSnackbar(result.message, 'success')
          this.formDialog = false
          await this.loadLimitations()
        } else {
          this.showSnackbar(result.message, 'error')
        }
      } finally {
        this.saving = false
      }
    },
    confirmDelete(limitation) {
      this.limitationToDelete = limitation
      this.deleteDialog = true
    },
    async deleteLimitation() {
      if (!this.limitationToDelete) return

      this.deleting = true
      try {
        const result = await deleteClientServicesLimitation(this.token, this.limitationToDelete.id)
        if (!result.error) {
          this.showSnackbar(result.message, 'success')
          this.deleteDialog = false
          await this.loadLimitations()
        } else {
          this.showSnackbar(result.message, 'error')
        }
      } finally {
        this.deleting = false
      }
    },
    showSnackbar(message, color = 'success') {
      this.snackbar = { show: true, message, color }
    }
  }
}
</script>
